import { Form, Formik } from 'formik'
import { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { BudgetContext } from '../../../../lib/Context'
import { Spend } from '../../../../lib/Types'
import { isCurrency } from '../../../../lib/Validations'
import Headline from '../../../Elements/Headline'
import { Modal } from '../../../Elements/Modal'
import Button from '../../../Formik/Button'
import Checkbox from '../../../Formik/Checkbox'
import Input from '../../../Formik/Input'
import Select from '../../../Formik/Select'
import SelectSearch, { SelectSearchItem } from '../../../Formik/SelectSearch'
import { useBudget, useBudgetReconciliations, useReconciliationExpenses, useUpdateBudgetReconciliation } from '../../lib/Hooks/Budget'

interface Props {
  toggleHide: () => void
  show: boolean
}

const AddReconciliation: FC<Props> = ({ toggleHide, show }) => {
  const { t } = useTranslation(['budget', 'common'])
  const { reconciliationYear } = useContext(BudgetContext)
  const { data: budget } = useBudget()
  const { data: spends } = useReconciliationExpenses(budget!, reconciliationYear)
  const { data: reconciliations } = useBudgetReconciliations(budget!, reconciliationYear)
  const { mutate: saveReconciliation, isPending: isLoading } = useUpdateBudgetReconciliation()
  const [spendsToRender, setSpendsToRender] = useState<{ spend: Spend, title: string }[]>([])

  const getSpendTitle = (spend: Spend) => {
    if (spend.parent_id) {
      const parent = spends!.find(s => s.id === spend.parent_id)

      if (parent) return `${parent.title} - ${spend.title}`
    }

    return spend.title
  }

  useEffect(() => {
    const spendsToSet = spends!.map(s => ({ spend: s, title: getSpendTitle(s) }))
    spendsToSet.sort((a, b) => a.title.localeCompare(b.title))
    setSpendsToRender(spendsToSet)
  }, [spends])

  return (
    <Modal show={show} onHide={toggleHide}>
      {(closeModal: any) => (
        <>
          <Headline className='mb-4'>{t('budget:addReconciliation.title')}</Headline>
          <Formik
            initialValues={{
              spend: '',
              amount: '',
              more: false,
            }}
            validationSchema={Yup.object({
              spend: Yup.number().required(t('common:validation.required')),
              amount: Yup.string()
                .transform((currentValue) => {
                  // First remove all danish thousand seperators (.) and then replace the danish delimiter seperator (,) with the english one (.).
                  // This way it's possible to save floats.
                  return currentValue.replaceAll('.', '').replaceAll(',', '.')
                })
                .required(t('common:validation.required'))
                .test(isCurrency),
            })}
            onSubmit={(values, { resetForm }) => {
              const { spend, amount, more } = values
              const amountTransformed = amount.replaceAll('.', '').replaceAll(',', '.')

              let reconciliation = reconciliations!.find(r => r.spend_id === parseInt(spend, 10))

              if (!reconciliation) {
                reconciliation = {
                  spend_id: parseInt(spend, 10),
                  budget_id: budget!.id,
                  year: reconciliationYear,
                  formula: `${amountTransformed}`,
                  amount: parseInt(amountTransformed, 10),
                }
              } else {
                reconciliation.formula = `${reconciliation.formula}+${amountTransformed}`
                reconciliation.amount += parseInt(amountTransformed, 10)
              }

              saveReconciliation(reconciliation)

              if (more !== true) {
                closeModal()
              } else {
                resetForm({
                  values: {
                    spend: '',
                    amount: '',
                    more: true,
                  },
                })
              }
            }}
          >
            <Form>

              <p className='mb-2'>{t('budget:addReconciliation.selectExpense')}</p>
              <SelectSearch name='spend' className="mb-3">
                {spendsToRender!.map(spend => (
                  <SelectSearchItem key={spend.spend.id} value={`${spend.spend.id}`}>{spend.title}</SelectSearchItem>
                ))}
              </SelectSearch>
              <p className='mb-2'>{t('budget:addReconciliation.amountLabel')}</p>
              <Input
                type='text'
                name='amount'
                placeholder={t('budget:shared.amountPlaceholder')}
                className='mb-4'
              />
              <div className="flex">
                <Button type='submit' loading={isLoading}>{t('budget:addReconciliation.addButton')}</Button>
                <div className="mt-3 ml-3">
                  <Checkbox name='more'>
                    {t('budget:addReconciliation.addMore')}
                  </Checkbox>
                </div>
              </div>
            </Form>
          </Formik>
        </>
      )
      }
    </Modal >
  )
}
export default AddReconciliation
